import { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "semantic-ui-react";
import FeatherIcon from "feather-icons-react";
import { useDispatch } from "react-redux";
import { importAlertState } from "src/redux/actions/alert";
import {
  buildCollectionEmailContactsPayload,
  collectionEmailsFromContacts,
  isValidCollectionEmail,
  normalizeCollectionEmailContactsFromAgenda,
} from "src/utils/collectionEmailContacts";
import "./ContactDetail/ContactDetail.scss";
import "./ContactCollection.scss";

const toContactRows = (contacts = []) =>
  contacts.map((contact, index) => ({
    id: `collection-contact-${index}-${String(contact.email || "").toLowerCase()}`,
    email: contact.email || "",
    contactName: contact.contactName || "",
  }));

function buildCollectionContactsDraft(rows = []) {
  return (rows || [])
    .map((row) => ({
      email: String(row?.email || "").trim(),
      contactName: String(row?.contactName || "").trim(),
    }))
    .filter((row) => row.email || row.contactName);
}

const contactsDraftSignature = (contacts = []) =>
  JSON.stringify(
    buildCollectionContactsDraft(contacts).map((c) => ({
      email: c.email.toLowerCase(),
      contactName: c.contactName,
    })),
  );

export const ContactCollection = ({ variables, loading, onChange, onSubmit }) => {
  const dispatch = useDispatch();
  const [collectionContacts, setCollectionContacts] = useState([]);
  const [emailErrors, setEmailErrors] = useState({});
  const isUserTypingRef = useRef(false);
  const lastExternalSignatureRef = useRef("");

  const normalizedInitialContacts = useMemo(
    () => normalizeCollectionEmailContactsFromAgenda(variables),
    [variables?.collectionEmailContacts, variables?.collectionEmails],
  );

  const externalSignature = useMemo(
    () => contactsDraftSignature(normalizedInitialContacts),
    [normalizedInitialContacts],
  );

  useEffect(() => {
    if (isUserTypingRef.current) return;
    if (externalSignature === lastExternalSignatureRef.current) return;

    lastExternalSignatureRef.current = externalSignature;
    setCollectionContacts(toContactRows(normalizedInitialContacts));
    setEmailErrors({});
  }, [externalSignature, normalizedInitialContacts]);

  const pushDraftToParent = (rows) => {
    const draft = buildCollectionContactsDraft(rows);
    onChange("collectionEmailContacts", draft);
    onChange("collectionEmails", collectionEmailsFromContacts(draft));
  };

  const addCollectionContact = () => {
    isUserTypingRef.current = true;
    setCollectionContacts((prev) => [
      ...prev,
      { id: `collection-contact-${Date.now()}`, email: "", contactName: "" },
    ]);
  };

  const updateCollectionContact = (id, field, value) => {
    isUserTypingRef.current = true;
    setCollectionContacts((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );

    if (field !== "email") return;

    const trimmedEmail = value.trim();
    setEmailErrors((prev) => {
      const nextErrors = { ...prev };
      if (!trimmedEmail || isValidCollectionEmail(trimmedEmail)) {
        delete nextErrors[id];
      } else {
        nextErrors[id] =
          "Correo invalido. Debe tener formato: texto@dominio.extension";
      }
      return nextErrors;
    });
  };

  const removeCollectionContact = (id) => {
    isUserTypingRef.current = true;
    setCollectionContacts((prev) => prev.filter((item) => item.id !== id));

    setEmailErrors((prev) => {
      const nextErrors = { ...prev };
      delete nextErrors[id];
      return nextErrors;
    });
  };

  const resetChanges = () => {
    isUserTypingRef.current = false;
    lastExternalSignatureRef.current = externalSignature;
    setCollectionContacts(toContactRows(normalizedInitialContacts));
    setEmailErrors({});
    pushDraftToParent(normalizedInitialContacts);
  };

  const validateBeforeSave = () => {
    const nextErrors = {};
    const invalidRows = [];

    collectionContacts.forEach((item) => {
      const trimmedEmail = String(item.email || "").trim();
      if (!trimmedEmail) return;

      if (!isValidCollectionEmail(trimmedEmail)) {
        nextErrors[item.id] =
          "Correo invalido. Debe tener formato: texto@dominio.extension";
        invalidRows.push(item);
      }
    });

    setEmailErrors(nextErrors);
    return invalidRows;
  };

  const handleSubmit = () => {
    const invalidRows = validateBeforeSave();
    if (invalidRows.length > 0) {
      dispatch(
        importAlertState({
          content: "Revise los correos de cobranza antes de guardar.",
          state: "error",
          duration: 5,
        }),
      );
      return;
    }

    const contacts = buildCollectionEmailContactsPayload(
      collectionContacts[0]?.email,
      collectionContacts[0]?.contactName,
      collectionContacts.slice(1),
    );
    isUserTypingRef.current = false;
    lastExternalSignatureRef.current = contactsDraftSignature(contacts);
    onChange("collectionEmailContacts", contacts);
    onChange("collectionEmails", collectionEmailsFromContacts(contacts));
    onSubmit({
      collectionEmailContacts: contacts,
      collectionEmails: collectionEmailsFromContacts(contacts),
    });
  };

  return (
    <div className="parametrizacion-tab-content collection-contacts">
      <div className="parametrizacion-section">
        <span className="parametrizacion-section-title">Responsables de cobranza</span>
        <p className="collection-contacts__hint">
          Indica el correo y el nombre de la persona encargada. El nombre se usará en el saludo
          del correo de cobranza. Si no configuras ninguno, se seguirán usando los correos
          generales del contacto.
        </p>

        {collectionContacts.length === 0 ? (
          <div className="collection-contacts__empty">
            Aun no hay correos de cobranza configurados para este contacto.
          </div>
        ) : (
          <div className="collection-contacts__list">
            {collectionContacts.map((item, index) => {
              const isPrimary = index === 0;
              const nameLabel = isPrimary
                ? "Nombre del contacto principal"
                : `Nombre del contacto secundario ${index}`;
              const emailLabel = isPrimary
                ? "Correo principal de cobranza"
                : `Correo secundario ${index}`;
              const badgeLabel = isPrimary ? "Principal" : `Secundario ${index}`;

              return (
                <div
                  key={item.id}
                  className={`collection-contact-card${isPrimary ? " collection-contact-card--primary" : ""}`}
                >
                  <div className="collection-contact-card__top">
                    <span className="collection-contact-card__badge">{badgeLabel}</span>
                    <button
                      type="button"
                      className="collection-contact-card__remove"
                      onClick={() => removeCollectionContact(item.id)}
                      title="Eliminar contacto de cobranza"
                      aria-label={`Eliminar ${badgeLabel.toLowerCase()}`}
                    >
                      <FeatherIcon icon="trash-2" size={16} />
                    </button>
                  </div>
                  <div className="collection-contact-card__body">
                    <div className="collection-contact-card__field">
                      <label className="label">{nameLabel}</label>
                      <input
                        className="parametrization-input"
                        placeholder="Ej. María González"
                        value={item.contactName || ""}
                        onChange={(e) =>
                          updateCollectionContact(item.id, "contactName", e.target.value)
                        }
                        type="text"
                      />
                    </div>
                    <div className="collection-contact-card__field">
                      <label className="label">{emailLabel}</label>
                      <input
                        className="parametrization-input"
                        placeholder="correo.cobranza@empresa.cl"
                        value={item.email || ""}
                        onChange={(e) =>
                          updateCollectionContact(item.id, "email", e.target.value)
                        }
                        type="email"
                        style={{
                          borderColor: emailErrors[item.id] ? "#dc2626" : undefined,
                        }}
                      />
                    </div>
                    {emailErrors[item.id] ? (
                      <div className="collection-contact-card__error">{emailErrors[item.id]}</div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button type="button" className="collection-contacts__add-btn" onClick={addCollectionContact}>
          <Icon name="plus" />
          Agregar correo de cobranza
        </button>
      </div>

      <div className="parametrization-buttons">
        <button
          type="button"
          className="parametrization-btn parametrization-btn--primary"
          disabled={loading}
          onClick={handleSubmit}
        >
          Guardar Cambios
        </button>
        <button
          type="button"
          className="parametrization-btn parametrization-btn--secondary"
          onClick={resetChanges}
        >
          Cancelar Cambios
        </button>
      </div>
    </div>
  );
};
